import type { RefObject } from 'react';
import type { AbilitySlot } from '../api/abilities';
import { BottomSheet, Dialog } from '../components/BottomSheet';
import { humanizeAbilityCode } from '../world/trainingMath';

export type AbilitySlotsSheetProps = {
  slots: AbilitySlot[];
  /**
   * Fires the moment "Find a school" is tapped on a filled row -- there is no confirm here
   * (fix-wave review finding: this sheet used to show a destructive confirm and a Shells-glyphed
   * "Confirm" button that fired NOTHING -- no mutation, nothing destroyed. A respec is a
   * *lesson*, and a lesson only ever happens at a school, which is the one place that knows which
   * ability is being learned and what it costs).
   *
   * SECOND correction (same fix-wave): this used to pass the tapped `slot` along so the caller
   * could carry it to Training School as a `?slot=` query param, mirroring `?element=`. That
   * cannot work -- no real navigation a player makes ever produces a URL carrying both params at
   * once (this screen doesn't know an element; a land hotspot's own link is built fresh and drops
   * any incoming slot). Corrected ruling: this callback carries no slot data at all any more --
   * it's a plain "go pick a school" hand-off (`HabitatScreen.tsx`'s `handleReplaceSlot` navigates
   * to the World hub). Choosing which slot a lesson targets is now entirely Training School's own
   * job (`TrainingScreen`'s `slotPicker`), so this sheet only ever views slots -- it never decided
   * a respec's outcome, even before this correction.
   *
   * THIRD correction (Task 12, ability-slots-and-training-schools): the button's own visible text
   * used to still read "Replace", which implies an in-place action is about to happen when what
   * actually happens is a bare navigate to `/world` -- exactly what the World tab already does,
   * with no land, school, or ability carried along. Relabelled to "Find a school" (kept, not cut:
   * it still saves the player the extra tap of closing this sheet before reaching for the World
   * tab themselves) so the label matches what tapping it actually does. See
   * docs/RULINGS.md "Ability slots and training schools".
   */
  onReplace: () => void;
  /** Optional so this component renders standalone (as the gallery, and this file's own test,
   *  both do) without a host wiring one up — same shape AllocateSheet/EquipmentSheet already
   *  require, just defaulted here rather than mandatory. */
  onClose?: () => void;
  /** The button that opened this sheet — see BottomSheet.tsx's own doc comment on why this is
   *  needed for reliable focus-return. */
  restoreFocusRef?: RefObject<HTMLElement | null>;
  /** Gallery-only — see BottomSheet.tsx's own doc comment. */
  portalContainer?: HTMLElement | null;
  /** Same habitat-dock-sheet convention as AllocateSheet/EquipmentSheet: `overlay` is the
   *  centered Bright Acorns map-sheet used for every real habitat dock mount; `sheet` (default)
   *  is the bottom drawer, kept only so a standalone render (gallery, this file's test) doesn't
   *  have to pass anything. */
  variant?: 'sheet' | 'overlay';
};

/**
 * SCREENS.md "Ability slots sheet": the player-facing view of a companion's six ability slots.
 * It makes NO decision about a respec any more (see `onReplace`'s own doc comment above for the
 * three corrections that got it here) -- it only shows what each slot holds. Six rows in slot
 * order -- locked (no action), empty (no action, nothing to replace), or filled (a "Find a
 * school" button that's a plain hand-off to the World hub, carrying no data).
 *
 * Purely presentational -- it renders whatever `slots` it's given and never fetches on its own,
 * same reasoning `AbilitiesTab` already documents for staying decoupled from data-fetching.
 */
export function AbilitySlotsSheet({
  slots, onReplace, onClose = () => {}, restoreFocusRef, portalContainer, variant = 'sheet',
}: AbilitySlotsSheetProps) {
  return (
    <BottomSheet
      open
      onOpenChange={(next) => { if (!next) onClose(); }}
      contentClassName="ability-slots-sheet"
      restoreFocusRef={restoreFocusRef}
      portalContainer={portalContainer}
      variant={variant}
    >
      <div className="sheet-header">
        <Dialog.Title asChild>
          <h2>Ability slots</h2>
        </Dialog.Title>
        <Dialog.Close asChild>
          <button type="button" className="sheet-close" aria-label="Close ability slots">Close</button>
        </Dialog.Close>
      </div>
      <Dialog.Description className="sr-only">
        Your companion&rsquo;s six ability slots, and where to replace what&rsquo;s learned in one.
      </Dialog.Description>

      <div className="sheet-body">
        <ul className="training-stat-list">
          {slots.map((slot) => (
            <li
              key={slot.slotIndex}
              data-testid={`ability-slot-${slot.slotIndex}`}
              className="training-stat-row"
            >
              {!slot.unlocked ? (
                <>
                  <span className="training-stat-name">Slot {slot.slotIndex + 1}</span>
                  <span className="training-stat-maxed">Unlocks at level {slot.unlocksAtLevel}</span>
                </>
              ) : slot.abilityCode == null ? (
                <>
                  <span className="training-stat-name">Slot {slot.slotIndex + 1}</span>
                  <span className="training-stat-maxed">Empty</span>
                </>
              ) : (
                <>
                  {/* QA 2026-09-23 (progress F24): a filled row carried the ability's name alone, so
                      slot 1 was the one row with no "Slot 1" on it. */}
                  <span className="training-stat-name">Slot {slot.slotIndex + 1} · {humanizeAbilityCode(slot.abilityCode)}</span>
                  <button
                    type="button"
                    className="btn-quiet"
                    aria-label={`Find a school to replace Slot ${slot.slotIndex + 1}`}
                    onClick={() => onReplace()}
                  >
                    Find a school
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      </div>
    </BottomSheet>
  );
}
